import React, { useCallback } from 'react';

// Styles & Assets
import stylesColumns from '../../../styles/Columns.module.css';

// Utils
import { areFrequenciesEqual } from '../../../services/Poll';

// Hooks
import { useTranslation } from 'react-i18next';

// Components
import TableConstants from '../../CreatePreconfigurationPage/TableConstants/TableConstants';
import BlueCell from '../../CreatePreconfigurationPage/BlueCell/BlueCell';
import AutocompletePreconfig from '../../Helpers/AutocompletePreconfig/AutocompletePreconfig';
import SelectFrequency from '../../Helpers/SelectFrequency/SelectFrequency';
import HourSelector from '../../UI/HourSelector/HourSelector';

// Data
import { DEFAULT_SHEET_PARAMETER_DATA } from '../../../data/ConstantsPreconfig';

// Interfaces
import { HH, mm } from '../../../interfaces/Time';
import { PreconfigSheetParameter } from '../../../interfaces/Preconfigurations';
import { Column } from '../../../interfaces/Columns';
import { PreconfigDispatch } from '../../../reducers/preconfig-reducer';
import { useFormPreconfig } from '../../../hooks/preconfig-hooks';
import useTranslate from '../../../hooks/useTranslate';
import { Parameter } from '../../../interfaces/Profile';

interface Props {
    sheetParameters: Parameter[];
    preconfigSheetParameters: PreconfigSheetParameter[];
    patientSheetParameters: PreconfigSheetParameter[];
    dispatchPreconfigSheetParams: React.Dispatch<PreconfigDispatch>;
}

const PatientPreconfigParameters: React.FC<Props> = ({
    sheetParameters,
    preconfigSheetParameters,
    patientSheetParameters,
    dispatchPreconfigSheetParams,
}) => {
    const { t } = useTranslation();
    const { translateMiddleware } = useTranslate();

    const {
        handleChangeConstant,
        handleChangeFreq,
        handleChangeValue,
        handleChangeReminder,
    } = useFormPreconfig(sheetParameters, dispatchPreconfigSheetParams);

    const options = sheetParameters.map(param => ({
        id: param.id,
        label: translateMiddleware(param.name_translations)?.text ?? param.name ?? param.id ?? ''
    }));

    const hasParamChanged = useCallback((
        paramID: PreconfigSheetParameter['sheet_parameter_id'],
        property: keyof PreconfigSheetParameter
    ) => {
        const preconfigParam = preconfigSheetParameters.find(p => p.sheet_parameter_id === paramID);
        const patientParam = patientSheetParameters.find(p => p.sheet_parameter_id === paramID);

        if (!preconfigParam && patientParam) return true;
        if (!patientParam) return false;

        return (preconfigParam?.[property] ?? null) !== (patientParam[property] ?? null);
    }, [preconfigSheetParameters, patientSheetParameters]);

    const isPatientFrequencySet = useCallback((paramID: PreconfigSheetParameter['sheet_parameter_id']) => {
        const preconfigParam = preconfigSheetParameters.find(p => p.sheet_parameter_id === paramID);
        const patientParam = patientSheetParameters.find(p => p.sheet_parameter_id === paramID);

        if (!preconfigParam && patientParam) return true;
        if (!preconfigParam || !patientParam) return false;

        return !areFrequenciesEqual(preconfigParam, patientParam);
    }, [preconfigSheetParameters, patientSheetParameters]);

    const isNewParameter = useCallback((paramID: PreconfigSheetParameter['sheet_parameter_id']) => {
        return (
            !preconfigSheetParameters.some(p => p.sheet_parameter_id === paramID) &&
            patientSheetParameters.some(p => p.sheet_parameter_id === paramID)
        );
    }, [preconfigSheetParameters, patientSheetParameters]);

    const styleSelects = {
        height: '35px',
        outline: 'none',
        padding: '8px 16px 8px 6px',
        fontSize: '0.8rem'
    };

    const COLUMNS: Column<PreconfigSheetParameter>[] = [
        {
            id: 0,
            name: () => t('vital_signs'),
            visible: true,
            sortable: false,
            element: constant =>
                <BlueCell show={isNewParameter(constant.sheet_parameter_id)}>
                    <AutocompletePreconfig
                        options={options}
                        value={options.find(o => o.id === constant.sheet_parameter_id)}
                        placeholder={t('select_vital_sign') as string}
                        getOptionDisabled={(o) => patientSheetParameters.some(p => p.sheet_parameter_id === o.id)}
                        handleChangeConstant={handleChangeConstant(constant.sheet_parameter_id ?? null)}
                    />
                </BlueCell>
        },
        {
            id: 1,
            name: () => t('minimum_critical_value'),
            visible: true,
            sortable: false,
            element: constant =>
                <BlueCell
                    show={
                        isNewParameter(constant.sheet_parameter_id) ||
                        hasParamChanged(constant.sheet_parameter_id, 'min_limit')
                    }
                >
                    <input
                        className={stylesColumns.inputText}
                        type='number'
                        value={constant.min_limit ?? ''}
                        onChange={handleChangeValue(constant.sheet_parameter_id ?? '', 'CHANGE_MIN_LIMIT')}
                    />
                </BlueCell>
        },
        {
            id: 2,
            name: () => t('maximum_critical_value'),
            visible: true,
            sortable: false,
            element: constant =>
                <BlueCell
                    show={
                        isNewParameter(constant.sheet_parameter_id) ||
                        hasParamChanged(constant.sheet_parameter_id, 'max_limit')
                    }
                >
                    <input
                        className={stylesColumns.inputText}
                        type='number'
                        value={constant.max_limit ?? ''}
                        onChange={handleChangeValue(constant.sheet_parameter_id ?? '', 'CHANGE_MAX_LIMIT')}
                    />
                </BlueCell>
        },
        {
            id: 3,
            name: () => t('frequency'),
            visible: true,
            sortable: false,
            element: constant =>
                <BlueCell
                    show={
                        isNewParameter(constant.sheet_parameter_id) ||
                        isPatientFrequencySet(constant.sheet_parameter_id)
                    }
                >
                    <SelectFrequency
                        pollFrequency={{
                            frecuency: constant.frecuency,
                            monthly_frequency_day: constant.monthly_frequency_day,
                            semanal_frequency: constant.semanal_frequency,
                            frequency_duration: constant.frequency_duration
                        }}
                        handleChangeFreq={handleChangeFreq(constant.sheet_parameter_id ?? '')}
                        style={styleSelects}
                    />
                </BlueCell>,
        },
        {
            id: 4,
            name: () => t('reminder'),
            visible: true,
            sortable: false,
            element: constant =>
                <BlueCell
                    show={
                        isNewParameter(constant.sheet_parameter_id) ||
                        hasParamChanged(constant.sheet_parameter_id, 'reminder_time')
                    }
                >
                    <HourSelector
                        hours={constant.reminder_time?.slice(0, 2) as HH || null}
                        minutes={constant.reminder_time?.slice(3, 5) as mm || null}
                        handleChange={handleChangeReminder(constant.sheet_parameter_id ?? '')}
                        style={{ border: 'none' }}
                    />
                </BlueCell>,
            classBody: stylesColumns.reminderCell
        },
    ];

    const uniqueRows = Array.from(
        new Map(
            patientSheetParameters.map((p, index) => [
                p.sheet_parameter_id ?? `temp-${index}`,
                p
            ])
        ).values()
    );

    const rowsWithExtra: PreconfigSheetParameter[] = [
        ...uniqueRows.map((p, index) => ({
            ...p,
            id: p.id ?? -(index + 1)
        })),
        {
            ...DEFAULT_SHEET_PARAMETER_DATA,
            id: 0
        }
    ];

    return (
        <TableConstants
            rows={rowsWithExtra}
            columns={COLUMNS}
            errorCells={{}}
        />
    );
};

export default PatientPreconfigParameters;